
import {onMounted, ref, computed} from 'vue';
import { throttle } from 'throttle-debounce';
import {
	GetBenefitWithIcon,
	GetProductById,
	GetProductSetById,
	GetProductSetItemsById,
	GetProductSetItemsWithBenefits
} from 'Apis/apis';
import Swiper, {Navigation, Pagination} from 'swiper';
import MicroModal from 'micromodal';

Swiper.use([Pagination, Navigation]);

export default {
	name: 'ProductSets',
	setup() {
		//SETUP OUR REFS
		const productData = ref(null);
		const productSets = ref([]);
		const selectedOption = ref('');
		const productSetItems = ref([]);
		const swiperWrapper = ref(null);
		const swiper = ref(null);
		const prevButton = ref(null);
		const nextButton = ref(null);
		const isHidden = ref(true);
		//MODAL SPECIFIC
		const productTitle = ref('');
		const productPrimaryImage = ref('');
		const productDimensions = ref('');
		const productDescription = ref('');
		const productBenefits = ref([]);
		const primaryImageDiv = ref(null);
		const titleEl = ref(null);
		const descriptionEl = ref(null);
		const dimensionsEl = ref(null);
		const columnEl = ref(null);
		const benefitsContainerHeight = ref(0);
		const moreProductImages = ref([]);
		const currentlySetModalItem = ref(null);
		const modalContentEl = ref(null);
		const itemIsLoading = ref(false);
		const translations = ref({});

		//GET PRODUCT ID FROM .NET PAGE
		let productId = document.querySelector('.js-product-sets').getAttribute('data-product-id') ? document.querySelector('.js-product-sets').getAttribute('data-product-id') : "";

		//GET INITIAL PRODUCT DATA
		async function getProductData(productId) {
			const rsp = await GetProductById(productId);
			if (rsp.ProductSets.length) {
				productSets.value = rsp.ProductSets;
				// Set selectedOption to the value of the first item in productSets
				selectedOption.value = productSets.value.length > 0 ? productSets?.value[0]?.Id : '';
				getProductSet();
			}
		}

		//GET PRODUCTSET FROM API
		async function getProductSet() {
			isHidden.value = true;

			if(!selectedOption.value) {
				return;
			}

			let productSet = await GetProductSetById(selectedOption.value);
			let modifiedArray = [];
			let promises = [];

			for (let [index, item] of productSet.ProductSetItems.entries()) {
				let imageObject = await GetProductSetItemsById(item.Id);
				item.ImagePath = imageObject.Images.length ? imageObject.Images[0].Thumbnails.filter(e => e.Title === 'medium')[0]?.Url : "";
				item.ImageAlt = imageObject.Images.length ? imageObject.Images[0].AlternativeText : "";
				item.Images = imageObject?.Images;
				modifiedArray.push(item);
				promises.push(new Promise((resolve) => {
					let img = new Image();
					img.onload = resolve;
					img.src = item.ImagePath;
				}));
			}

			productSetItems.value = modifiedArray;
			await Promise.all(promises);
			setupProductSetItemsSwiper();
			isHidden.value = false;
		}

		const getIndexOfCurrent = computed(() => {
			return (productSetItems?.value?.findIndex(obj => obj?.Id === currentlySetModalItem?.value?.Id)) + 1;
		});

		//WHEN THUMBNAILS IN THE MODAL ARE CLICKED RUN THIS TO SWAP OUT PRIMARY IMAGE URL
		let swapImageUrl = (itemObject) => {
			productPrimaryImage.value = itemObject.Url;
		}
		let productSetItemsSwiper;

		//SETUP SWIPER
		let setupProductSetItemsSwiper = () => {
			//DESTROY SWIPER IF IT EXISTS ALREADY

			if (typeof productSetItemsSwiper !== 'undefined' && productSetItemsSwiper !== null) {
				productSetItemsSwiper.destroy();
				productSetItemsSwiper = null;
			}

			//REINITIALIZE SWIPER
			productSetItemsSwiper = new Swiper(swiper.value, {
				slidesPerView: 1,
				spaceBetween: 0,

				// Navigation arrows
				navigation: {
					nextEl: nextButton.value,
					prevEl: prevButton.value,
				},

				breakpoints: {
					0: {
						slidesPerView: 1,
						spaceBetween: 0,
					},
					768: {
						slidesPerView: 5,
						spaceBetween: 10,
					}
				}
			});
		};

		//SET THE HEIGHT OF VUE3 PERFECT SCROLLBAR
		let getCalculatedHeight = () => {
			benefitsContainerHeight.value = (columnEl.value.clientHeight - 20) - (titleEl.value.offsetHeight + descriptionEl.value.offsetHeight + dimensionsEl.value.offsetHeight);
		};

		//LOAD PREVIOUS PRODUCT ITEM
		let loadPrev = () => {
			let currentIndex = productSetItems.value.findIndex(obj => obj.Id === currentlySetModalItem.value.Id);
			let prevIndex = (currentIndex - 1 + productSetItems.value.length) % productSetItems.value.length;
			let prevItem = productSetItems.value[prevIndex];
			drawModalContent(prevItem);
		}

		//LOAD NEXT PRODUCT ITEM
		let loadNext = () => {
			let currentIndex = productSetItems.value.findIndex(obj => obj.Id === currentlySetModalItem.value.Id);
			let nextIndex = (currentIndex + 1) % productSetItems.value.length;
			let nextItem = productSetItems.value[nextIndex];
			drawModalContent(nextItem);
		}


		//DRAW THE MODAL CONTENT - USES A FED IN PRODUCTSETITEM
		let drawModalContent = async(item) => {
			itemIsLoading.value = true;
			currentlySetModalItem.value = item;
			window.removeEventListener('resize', getCalculatedHeight);
			productTitle.value = item.DisplayTitle;
			productDescription.value =  item.ItemDescription;
			productPrimaryImage.value = item.Images[0]?.Url;
			productDimensions.value = item.Dimensions;

			//GET BENEFITS
			let rsp = await GetProductSetItemsWithBenefits(item.Id);
			let promises = [];
			let modifiedArray = [];

			for (let [index, item] of rsp.Benefits.entries()) {
				let iconObject = await GetBenefitWithIcon(item.Id);
				if(iconObject?.Icon && iconObject?.Icon?.length) {
					item.Icon = iconObject?.Icon[0];
					modifiedArray.push(item);
					promises.push(new Promise((resolve) => {
						let img = new Image();
						img.onload = resolve;
						img.src = item?.Icon?.Url ? item?.Icon?.Url : "";
					}));
				}
			}

			productBenefits.value = modifiedArray;
			await Promise.all(promises);

			//ASSIGN EXTRA PRODUCT ITEM SET IMAGES TO AN ARRAY
			moreProductImages.value = item.Images;

			//add anything that happens after image load here
			getCalculatedHeight();
			window.addEventListener('resize', getCalculatedHeight);
			itemIsLoading.value = false;
		}

		//WHEN THE MODAL OPENS WE RUN THIS CALLBACK
		let openCallback = async(item) => {
			drawModalContent(item);
		};

		//WHEN THE MODAL CLOSES WE CLEANUP SOME STUFF SO WE DONT SEE GLIMPSES OF THE PREVIOUS ITEM ON LOAD
		let closeCallback = () => {
			productTitle.value = null;
			productDescription.value = null;
			productPrimaryImage.value = null;
			productDimensions.value = null;
			productBenefits.value = [];
			moreProductImages.value = [];
		};

		//FIRED WHEN A PRODUCTSET ITEM IS CLICKED IN THE TEMPLATE
		function openModal(item) {
			MicroModal.show('productSetsModal', {
				onShow: modal => openCallback(item),
				onClose: modal => closeCallback(),
				disableFocus: true,
			});
		}

		//WHEN OUR COMPONENT INITIALLY LOADS GET THE PRODUCT DATA AND INITIALIZE THE MODAL
		onMounted(() => {
			const translationEl = document.querySelector('.js-product-sets');
			const translationValue = translationEl.getAttribute('data-translations');
			if (translationEl && translationValue) {
				translations.value = JSON.parse(translationValue);
			}
			if(productId) {
				getProductData(productId);
			}
			MicroModal.init();
		});

		return {
			productData,
			productSets,
			selectedOption,
			getProductSet,
			productSetItems,
			openModal,
			swiper,
			swiperWrapper,
			nextButton,
			prevButton,
			isHidden,
			//MODAL SPECIFIC
			productTitle,
			productPrimaryImage,
			productDimensions,
			productDescription,
			productBenefits,
			primaryImageDiv,
			titleEl,
			descriptionEl,
			dimensionsEl,
			columnEl,
			benefitsContainerHeight,
			moreProductImages,
			swapImageUrl,
			loadNext,
			loadPrev,
			currentlySetModalItem,
			modalContentEl,
			itemIsLoading,
			getIndexOfCurrent,
			translations
		};
	},
};
